<!-- eslint-disable unicorn/no-new-array -->
<script setup lang="ts">
const { sortedContents, onFeedViewMounted, onFeedViewUnmounted, onUpdatedViewModeSettings } = useFeedView()
const { state: viewModeState, previewScale } = useViewMode()

const container = useTemplateRef("container")
const { width } = useElementSize(container)
const throttledWidth = refThrottled(width, 1000 / 30)

/**
 * This method lays out the files in a masonry layout style in the spirit of
 * Pinterest, etc.
 * @param nCols Number of columns to generate columns for
 */
function fileColumns(nCols: number) {
  if (!nCols)
    return []
  const columns = Array.from(new Array(nCols), _ => ({} as Record<string, InterfaceFile>))
  const heights = new Array<number>(nCols).fill(0)
  for (const file of sortedContents.value) {
    let v = 1
    if (file.mediaWidthPx() && file.mediaHeightPx())
      v = file.mediaHeightPx()! / file.mediaWidthPx()!
    else if (file.mediaWidthCm() && file.mediaHeightCm())
      v = file.mediaHeightCm()! / file.mediaWidthCm()!
    const iCol = heights.indexOf(Math.min(...heights))
    heights[iCol] += v
    columns[iCol][file.fileId] = file
  }
  return columns
}
const implicitWidth = computed(() => {
  const scale = previewScale.value
  const width = 2 ** (5 + 5 * scale)
  return width
})
const numColumns = computed(() => Math.max(1, Math.round(throttledWidth.value / implicitWidth.value)))

const previewWidth = computed(() => throttledWidth.value / numColumns.value)

onMounted(() => onFeedViewMounted())
onUnmounted(() => onFeedViewUnmounted())

watch(() => viewModeState.value.current, onUpdatedViewModeSettings)
</script>

<template>
  <collection-scroll-container vertical>
    <div class="spacer" />
    <div class="notes-container">
      <collection-folder-notes />
    </div>
    <div ref="container" class="flex gap-0.5 p-0.5">
      <div
        v-for="(column, key) in fileColumns(numColumns)"
        :key
        class="w-full flex flex-col gap-0.5"
      >
        <collection-feed-file
          v-for="(file, fileId) in column"
          :key="fileId"
          :file
          :preview-width
          class="group relative w-full cursor-pointer"
        />
      </div>
    </div>
  </collection-scroll-container>
</template>

<style lang="sass" scoped>
@import '~/assets/styles/generated/variables.sass'

.spacer
  @apply pointer-events-none w-full
  @apply h-[#{$nav-h-default}] mb-[#{$nav-gap-pills}]

.notes-container
  @apply px-[#{$page-icon-p-main}] py-1
</style>
